import React, { useCallback, useRef, useState } from 'react';
import { FileText, Loader2, Paperclip, ShieldCheck, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { DOCUMENT_BUCKET, DOCUMENT_KINDS, DocumentKind, ListingDocument, safeFileName } from '@/lib/listingSettings';

interface Props {
  value: ListingDocument[];
  onChange: (docs: ListingDocument[]) => void;
  max: number;
  maxMb: number;
  accept: string[];
}

const prettySize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Read the first bytes so a renamed .exe/.zip never reaches storage. */
const looksLikePdf = async (file: File) => {
  try {
    const head = new Uint8Array(await file.slice(0, 5).arrayBuffer());
    return String.fromCharCode(...head) === '%PDF-';
  } catch {
    return false;
  }
};

/**
 * PDF uploader for project postings.
 *
 * Files go to the PRIVATE `listing-documents` bucket through a server-issued
 * signed upload token, then the `listing-media` edge function re-checks the
 * real file signature and size and deletes anything that is not a true PDF.
 * Only the storage path is kept — never a public URL.
 */
const DocumentUploader: React.FC<Props> = ({ value, onChange, max, maxMb, accept }) => {
  const { t } = useI18n();
  const { profile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const maxBytes = maxMb * 1024 * 1024;

  const upload = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;
      setError('');

      if (!profile) return setError(t('upload.errAuth'));
      if (value.length + list.length > max) return setError(t('docs.errMax', { max }));

      const badType = list.find((f) => {
        const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
        return !accept.includes(f.type) && !isPdf;
      });
      if (badType) return setError(t('docs.errType'));

      const tooBig = list.find((f) => f.size > maxBytes);
      if (tooBig) return setError(t('docs.errSize', { mb: maxMb }));

      setBusy(true);
      const added: ListingDocument[] = [];
      for (const file of list) {
        if (!(await looksLikePdf(file))) {
          setError(t('docs.errSignature'));
          continue;
        }

        // 1. Ask the server for a one-time upload token (validates size + extension).
        const { data: signed, error: signErr } = await supabase.functions.invoke('listing-media', {
          body: { action: 'sign-upload', filename: file.name, size: file.size },
        });
        if (signErr || !signed?.path || !signed?.token) {
          setError(signed?.error === 'file_too_large' ? t('docs.errSize', { mb: maxMb }) : t('upload.errFailed'));
          continue;
        }

        // 2. Upload straight into the private bucket with that token.
        const { error: upErr } = await supabase.storage
          .from(DOCUMENT_BUCKET)
          .uploadToSignedUrl(signed.path, signed.token, file, { contentType: 'application/pdf' });
        if (upErr) {
          setError(t('upload.errFailed'));
          continue;
        }

        // 3. Server re-verifies the stored bytes and removes the file if it lies.
        const { data: check } = await supabase.functions.invoke('listing-media', {
          body: { action: 'verify', path: signed.path },
        });
        if (!check?.ok) {
          setError(check?.error === 'signature_mismatch' ? t('docs.errSignature') : t('docs.errSize', { mb: maxMb }));
          continue;
        }

        added.push({
          path: signed.path,
          name: safeFileName(file.name),
          size: file.size,
          kind: 'other',
          type: 'application/pdf',
        });
      }
      setBusy(false);
      if (added.length) onChange([...value, ...added]);
    },
    [accept, max, maxBytes, maxMb, onChange, profile, t, value]
  );

  const setKind = (i: number, kind: DocumentKind) =>
    onChange(value.map((d, idx) => (idx === i ? { ...d, kind } : d)));

  const removeAt = async (i: number) => {
    const doc = value[i];
    onChange(value.filter((_, idx) => idx !== i));
    try {
      await supabase.functions.invoke('listing-media', { body: { action: 'delete', path: doc.path } });
    } catch (_e) {
      /* entry already removed from the form */
    }
  };

  return (
    <div>
      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-5 text-center">
        <FileText className="mx-auto h-8 w-8 text-slate-400" />
        <p className="mt-2 text-sm font-semibold text-slate-700">{t('docs.drop')}</p>
        <p className="mt-1 text-xs text-slate-400">{t('docs.limits', { max, mb: maxMb })}</p>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy || value.length >= max}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
        >
          <Paperclip className="h-4 w-4" /> {t('docs.browse')}
        </button>
        {busy && (
          <p className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('upload.uploading')}
          </p>
        )}
        <p className="mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700">
          <ShieldCheck className="h-3.5 w-3.5" /> {t('docs.private')}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) upload(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {error && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {value.length > 0 && (
        <ul className="mt-3 space-y-2">
          {value.map((d, i) => (
            <li key={d.path} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
              <FileText className="h-4 w-4 shrink-0 text-red-500" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{safeFileName(d.name)}</span>
              <span className="text-xs text-slate-400">{prettySize(d.size)}</span>
              <select
                value={d.kind || 'other'}
                onChange={(e) => setKind(i, e.target.value as DocumentKind)}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs font-semibold text-slate-700"
              >
                {DOCUMENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`docs.kind.${k}`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={t('upload.remove')}
                className="rounded-full bg-slate-100 p-1.5 text-slate-500 hover:bg-red-100 hover:text-red-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DocumentUploader;
